import { type SelectItem } from '../../types'
import { type BracketGameData, type GameTiebreakers, isNotDefined, type LadderGameData } from '@terros/common'
import { DateTime } from 'luxon'
import { z } from 'zod'

const TIE_BREAKER_START_DATE_OPTIONS = ['year', 'quarter', 'competition', 'custom'] as const
export const TiebreakerStartDateOptionSchema = z.enum(TIE_BREAKER_START_DATE_OPTIONS)
export type TiebreakerStartDateOption = z.infer<typeof TiebreakerStartDateOptionSchema>
export const TIE_BREAKER_START_DATE_OPTIONS_DATA: SelectItem<TiebreakerStartDateOption>[] = [
  {
    label: 'Since start of competition',
    value: 'competition',
  },
  {
    label: 'Since start of quarter',
    value: 'quarter',
  },
  {
    label: 'Since start of year',
    value: 'year',
  },
  {
    label: 'Custom start date',
    value: 'custom',
  },
]

export const isSameDate = (date1: string | number, date2: DateTime): boolean => {
  if (typeof date1 === 'string') {
    return date1 === date2.toISO()
  }
  return date1 === date2.toMillis()
}

export const getTiebreakerStartDate = (option: TiebreakerStartDateOption, gameStart: number): number | undefined => {
  const now = DateTime.now()
  switch (option) {
    case 'year':
      return now.startOf('year').toMillis()
    case 'quarter':
      return now.startOf('quarter').toMillis()
    case 'competition':
      return gameStart
    case 'custom':
      return gameStart
  }
}

export const stringifyTiebreakerStartDate = (
  option: TiebreakerStartDateOption,
  gameStart: number
): string | undefined => {
  const now = DateTime.now()
  switch (option) {
    case 'year':
      return `${now.startOf('year').toLocaleString(DateTime.DATETIME_FULL)}`
    case 'quarter':
      return `${now.startOf('quarter').toLocaleString(DateTime.DATETIME_FULL)}`
    case 'competition':
      return `${DateTime.fromMillis(gameStart).toLocaleString(DateTime.DATETIME_FULL)}`
    case 'custom':
      return `${DateTime.fromMillis(gameStart).toLocaleString(DateTime.DATETIME_FULL)}`
  }
}

export const toTiebreakerStartDateOption = (
  tiebreakerStartDate: number | string | undefined,
  gameStart: number
): TiebreakerStartDateOption => {
  if (isNotDefined(tiebreakerStartDate)) return 'competition'
  if (isSameDate(tiebreakerStartDate, DateTime.fromMillis(gameStart))) return 'competition'
  const now = DateTime.now()
  if (isSameDate(tiebreakerStartDate, now.startOf('year'))) return 'year'
  if (isSameDate(tiebreakerStartDate, now.startOf('quarter'))) return 'quarter'
  return 'custom'
}

type GameHasTiebreaker = LadderGameData | BracketGameData
export const getNewTiebreakerStartDate = (
  previous: Pick<GameHasTiebreaker, 'startDate' | 'tiebreakers'>,
  newStartDate: number
): GameTiebreakers | undefined => {
  const { tiebreakers } = previous
  if (isNotDefined(tiebreakers)) return
  const { startTime } = tiebreakers
  if (isNotDefined(startTime)) return tiebreakers
  if (newStartDate !== startTime) return tiebreakers
  return { ...tiebreakers, startTime: newStartDate }
}
